import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  Select,
  CircularProgress,
  Tooltip
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import apiUrl from '../config';
import LayoutDashboard from "../layout/LayoutDashboard";
import "../CSS/Utilisateurs.css";

const Utilisateurs = () => {

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog suppression
  const [openDialog, setOpenDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Dialog ajout
  const [openAddUser, setOpenAddUser] = useState(false);
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('USER');

  // Rôle en cours de modification (pour le spinner sur la ligne)
  const [updatingRoleId, setUpdatingRoleId] = useState(null);

  // ID de l'admin connecté (pour désactiver ses propres contrôles)
  const [currentUserId, setCurrentUserId] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  /* =====================
     RÉCUPÉRER L'ID DE L'ADMIN CONNECTÉ
  ===================== */
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(decoded.user_id);
      }
    } catch {}
  }, []);

  /* =====================
     CHARGER LES UTILISATEURS
  ===================== */
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${apiUrl}/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error(error);
      setNotification({
        open: true,
        message: 'Erreur lors du chargement des utilisateurs',
        severity: 'error'
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* =====================
     SUPPRIMER UN UTILISATEUR
  ===================== */
  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDialog(true);
  };

  const handleDeleteUser = async (email) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${apiUrl}/delete-user?email=${email}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(users.filter(u => u.email !== email));
      setNotification({ open: true, message: 'Utilisateur supprimé', severity: 'success' });
    } catch (error) {
      const msg = error.response?.data?.message || 'Erreur lors de la suppression';
      setNotification({ open: true, message: msg, severity: 'error' });
    }
    setOpenDialog(false);
  };

  /* =====================
     MODIFIER LE RÔLE
  ===================== */
  const handleRoleChange = async (userId, newRole) => {

    // Sauvegarde l'ancien rôle pour rollback si erreur
    const previousUsers = [...users];

    // Mise à jour immédiate dans l'interface (optimiste)
    setUsers(prev =>
      prev.map(u => u.id === userId ? { ...u, role: newRole, type: newRole } : u)
    );
    setUpdatingRoleId(userId);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${apiUrl}/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification({
        open: true,
        message: `Rôle mis à jour : ${newRole}`,
        severity: 'success'
      });
    } catch (error) {
      // Annule le changement en cas d'erreur
      setUsers(previousUsers);
      const msg = error.response?.data?.message || 'Erreur lors de la modification du rôle';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setUpdatingRoleId(null);
    }
  };

  /* =====================
     AJOUTER UN UTILISATEUR
  ===================== */
  const handleAddUser = async () => {
    if (!nom || !prenom || !email) {
      setNotification({
        open: true,
        message: 'Tous les champs sont obligatoires',
        severity: 'error'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/register`,
        { nom, prenom, email, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotification({ open: true, message: 'Utilisateur ajouté', severity: 'success' });
      setOpenAddUser(false);
      setNom('');
      setPrenom('');
      setEmail('');
      setRole('USER');

      // Recharger la liste
      fetchUsers();

    } catch (error) {
      const msg = error.response?.data?.message || 'Erreur lors de la création';
      setNotification({ open: true, message: msg, severity: 'error' });
    }
  };

  /* =====================
     RENDU
  ===================== */
  return (
    <LayoutDashboard>
      <div className="utilisateurs-page">

        {/* HEADER */}
        <div className="header">
          <Typography variant="h1">
            Gestion des Utilisateurs
          </Typography>
        </div>

        {/* BOUTON AJOUTER */}
        <div className="utilisateurs-actions">
          <Button
            variant="contained"
            className="utilisateurs-btn"
            onClick={() => setOpenAddUser(true)}
          >
            + Ajouter un utilisateur
          </Button>
        </div>

        {/* TABLEAU */}
        <TableContainer className="utilisateurs-table" sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {

                  const isSelf = String(user.id) === String(currentUserId);

                  return (
                    <TableRow key={user.id}>

                      {/* NOM */}
                      <TableCell sx={{ fontWeight: 600 }}>
                        {user.nom}
                      </TableCell>

                      {/* PRÉNOM */}
                      <TableCell>
                        {user.prenom}
                      </TableCell>

                      {/* EMAIL */}
                      <TableCell>
                        {user.email}
                      </TableCell>

                      {/* RÔLE — menu déroulant */}
                      <TableCell>
                        <Tooltip
                          title={isSelf ? "Vous ne pouvez pas modifier votre propre rôle" : ""}
                          arrow
                        >
                          <span>
                            <Select
                              size="small"
                              value={user.role || user.type || 'USER'}
                              disabled={isSelf || updatingRoleId === user.id}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              sx={{
                                minWidth: 110,
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                borderRadius: '8px',
                                // Couleur selon le rôle
                                color: (user.role || user.type) === 'ADMIN'
                                  ? '#2563eb'
                                  : '#374151',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: (user.role || user.type) === 'ADMIN'
                                    ? '#2563eb'
                                    : '#d1d5db'
                                }
                              }}
                            >
                              <MenuItem value="USER">USER</MenuItem>
                              <MenuItem value="ADMIN">ADMIN</MenuItem>
                            </Select>

                            {/* Spinner pendant la mise à jour */}
                            {updatingRoleId === user.id && (
                              <CircularProgress
                                size={16}
                                sx={{ ml: 1, verticalAlign: 'middle' }}
                              />
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* STATUT */}
                      <TableCell>
                        <span className={
                          user.compte_status === "Compte validé"
                            ? "status-ok"
                            : "status-bad"
                        }>
                          {user.compte_status}
                        </span>
                      </TableCell>

                      {/* SUPPRIMER */}
                      <TableCell align="center">
                        <Tooltip
                          title={isSelf ? "Vous ne pouvez pas vous supprimer vous-même" : "Supprimer"}
                          arrow
                        >
                          <span>
                            <IconButton
                              onClick={() => openDeleteDialog(user)}
                              disabled={isSelf}
                              sx={{
                                color: isSelf ? '#9ca3af' : 'red',
                                transition: 'transform .15s',
                                '&:hover': { transform: 'scale(1.15)' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </div>

      {/* =====================
          DIALOG SUPPRESSION
      ===================== */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          {userToDelete && (
            <Typography>
              Supprimer <strong>{userToDelete.nom} {userToDelete.prenom}</strong> ({userToDelete.email}) ?
              <br />
              <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                Cette action est irréversible.
              </span>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDeleteUser(userToDelete.email)}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================
          DIALOG AJOUT
      ===================== */}
      <Dialog
        open={openAddUser}
        onClose={() => setOpenAddUser(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ajouter un utilisateur</DialogTitle>
        <DialogContent>

          <TextField
            label="Nom"
            fullWidth
            margin="normal"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
          />

          <TextField
            label="Prénom"
            fullWidth
            margin="normal"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
          />

          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            select
            label="Rôle"
            fullWidth
            margin="normal"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <MenuItem value="USER">USER</MenuItem>
            <MenuItem value="ADMIN">ADMIN</MenuItem>
          </TextField>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddUser(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAddUser}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================
          NOTIFICATION
      ===================== */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>

    </LayoutDashboard>
  );
};

export default Utilisateurs;